/* eslint-disable style/jsx-one-expression-per-line */
import type { OnboardingConfigItem } from '../../../authentication/types/types.auth';
import { Box, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { UserContext } from '@/shared/contexts/UserContext';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import {
  getOnboardingIntent,
  OnboardingAction,
} from '@shared/utils/onboardingIntent';
import { trackPageView } from '../../../../third-party/ga';
import {
  AcquisitionSourceStep,
  DealInsightStep,
  HourlyRateStep,
} from './steps';

export interface OnboardingStepProps {
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
}

export interface OnboardingWizardProps {
  fullScreen?: boolean;
  onComplete?: (options?: { hasDealInsights?: boolean }) => void;
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  fullScreen = false,
  onComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  const { user, refetchUser } = useContext(UserContext);
  const { closeMessage } = useMessageService();
  const api = useApi();
  const [initialising, setInitialising] = useState(true);
  const initialisedRef = useRef(false);

  const [onboardingData, setOnboardingData] = useState<{
    welcomeCompleted?: boolean;
    dealInsights?: Array<{
      companyName: string;
      department: string;
      year: number;
      acv?: number;
      outcome?: 'won' | 'lost';
    }>;
    skippedSteps?: string[];
  }>({
    skippedSteps: [],
  });

  // All kandir users are contributors — fixed three-step flow
  const steps = useMemo<('hourlyRate' | 'dealInsight' | 'source')[]>(
    () => ['hourlyRate', 'dealInsight', 'source'],
    [],
  );

  const totalSteps = steps.length;

  const stepConfig = {
    hourlyRate: {
      title: 'Set Your Consulting Rate',
      path: '/onboarding/hourly-rate',
    },
    dealInsight: {
      title: 'Share Your Deal Experience',
      path: '/onboarding/deal-insights',
    },
    source: {
      title: 'How Did You Hear About Us?',
      path: '/onboarding/acquisition-source',
    },
  };

  const currentStepKey = steps[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  useEffect(() => {
    const stepKey = steps[currentStep];
    trackPageView(
      stepConfig[stepKey].path,
      `Onboarding - ${stepConfig[stepKey].title}`,
    );
  }, [currentStep, steps]);

  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    refetchUser().finally(() => setInitialising(false));
  }, []);

  const submitOnboardingCompletion = useCallback(async () => {
    if (!user) {
      return;
    }
    const checklist = user.onboardingConfig || [];
    const updatedChecklist = checklist.length > 0
      ? checklist.map(
          (item: OnboardingConfigItem, idx: number) =>
            idx === 0 ? { ...item, completed: true } : item,
        )
      : [{ completed: true }];

    try {
      const acquisitionSourceData = sessionStorage.getItem('acquisitionSource');
      let source: string | undefined;
      let referer: string | undefined;

      if (acquisitionSourceData) {
        try {
          const sourceData = JSON.parse(acquisitionSourceData);
          source = sourceData.source;
          referer = sourceData.sourceDetails;
          sessionStorage.removeItem('acquisitionSource');
        }
        catch {
          // Silent fail
        }
      }

      const payload: any = {
        ...updatedChecklist,
      };

      if (source) {
        payload.source = source;
      }
      if (referer) {
        payload.referer = referer;
      }

      payload.onboardingStepData = {
        welcomeCompleted: onboardingData.welcomeCompleted,
        dealInsights: onboardingData.dealInsights,
        skippedSteps: onboardingData.skippedSteps,
      };

      await api.put(
        `${import.meta.env.VITE_API_BASE_URL}/user/${user.id}/onboarding`,
        payload,
      );
    }
    catch (error) {
    }
  }, [api, user, onboardingData]);

  const handleClose = async () => {
    await submitOnboardingCompletion();

    // All kandir users are contributors
    try {
      await api.post('subscription/assign-contributor', {});
    } catch {
      // Non-fatal
    }

    try {
      await refetchUser();
    } catch {
      // Non-fatal — user data will refresh on next navigation
    }
    closeMessage();

    const intent = getOnboardingIntent();

    if (
      intent?.action === OnboardingAction.CreateIntelligenceRequest
      && intent.companyId
      && intent.companyName
    ) {
      const actionData = {
        type: 'createIntelligenceRequest',
        companyId: intent.companyId,
        companyName: intent.companyName,
      };

      sessionStorage.setItem(
        'completedOnboardingAction',
        JSON.stringify(actionData),
      );
    }

    const hasDealInsights = (onboardingData.dealInsights?.length ?? 0) > 0;
    onComplete?.({ hasDealInsights });
  };

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1));
  }, [totalSteps]);

  const goBack = useCallback(() => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleDealInsightsChange = useCallback(
    (dealInsights: Array<{
      companyName: string;
      department: string;
      year: number;
      acv?: number;
      outcome?: 'won' | 'lost';
    }>) => {
      setOnboardingData(prev => ({
        ...prev,
        dealInsights,
      }));
    },
    [],
  );

  const handleDealInsightsComplete = useCallback(() => {
    if (!onboardingData.dealInsights || onboardingData.dealInsights.length === 0) {
      setOnboardingData(prev => ({
        ...prev,
        skippedSteps: [...(prev.skippedSteps || []), 'dealInsight'],
      }));
    }
    goNext();
  }, [goNext, onboardingData.dealInsights]);

  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 100 : -100,
    }),
    center: {
      opacity: 1,
      x: 0,
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir < 0 ? 100 : -100,
    }),
  };

  const renderStep = () => {
    switch (currentStepKey) {
      case 'hourlyRate':
        return (
          <HourlyRateStep
            onNext={goNext}
            onSkip={goNext}
          />
        );
      case 'dealInsight':
        return (
          <DealInsightStep
            onNext={handleDealInsightsComplete}
            onBack={currentStep > 0 ? goBack : undefined}
            onSkip={handleDealInsightsComplete}
            onChange={handleDealInsightsChange}
          />
        );
      case 'source':
        return (
          <AcquisitionSourceStep
            onNext={handleClose}
            onBack={goBack}
            onSkip={handleClose}
          />
        );
      default:
        return null;
    }
  };

  if (initialising) {
    return <LoadingSpinner variant="circular" size="large" message="Setting up your account..." fullScreen />;
  }

  return (
    <Box
      className="onboarding-modal"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: fullScreen ? undefined : '85vh',
        minHeight: fullScreen ? undefined : '500px',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        ...(fullScreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }),
      }}
    >
      <Box
        sx={{
          'background':
            'linear-gradient(135deg, #c05a3a 0%, #5a8eeb 50%, #d4825e 100%)',
          'borderRadius': fullScreen ? 0 : '12px 12px 0 0',
          'px': { xs: 2.5, sm: 3, md: 4 },
          'py': { xs: 2.5, sm: 3 },
          'position': 'relative',
          'boxShadow': '0 4px 20px rgba(192, 90, 58, 0.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.95)',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
            }}
          >
            Kandir Onboarding - Step {currentStep + 1} of {totalSteps}
          </Typography>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
              }}
            >
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
        </Box>

        {currentStep === 0 && (
          <Box sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
            <Typography
              sx={{
                color: 'white',
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '1.875rem' },
                lineHeight: 1.2,
                textShadow: '0 2px 4px rgba(0,0,0,0.12)',
                letterSpacing: '-0.01em',
              }}
            >
              Welcome to Kandir!
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                fontWeight: 500,
                mt: 0.5,
              }}
            >
              Just a few quick steps to get you set up.
            </Typography>
          </Box>
        )}

        <Typography
          variant="h5"
          sx={{
            color: currentStep === 0 ? 'rgba(255,255,255,0.75)' : 'white',
            fontWeight: currentStep === 0 ? 600 : 700,
            mb: 2.5,
            position: 'relative',
            zIndex: 1,
            fontSize: { xs: currentStep === 0 ? '0.9375rem' : '1.25rem', sm: currentStep === 0 ? '1rem' : '1.5rem' },
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            letterSpacing: currentStep === 0 ? '0.04em' : 'inherit',
            textTransform: currentStep === 0 ? 'uppercase' : 'none',
          }}
        >
          {stepConfig[currentStepKey].title}
        </Typography>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: 10,
              height: 10,
              overflow: 'hidden',
              boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.15)',
              position: 'relative',
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{
                duration: 0.6,
                ease: [0.4, 0, 0.2, 1],
              }}
              style={{
                height: '100%',
                background:
                  'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 100%)',
                borderRadius: 10,
                boxShadow:
                  '0 0 12px rgba(255,255,255,0.5), inset 0 1px 2px rgba(255,255,255,0.3)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  'position': 'absolute',
                  'top': 0,
                  'left': 0,
                  'right': 0,
                  'bottom': 0,
                  'background':
                    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                  'animation': 'shimmer 2s infinite',
                  '@keyframes shimmer': {
                    '0%': { transform: 'translateX(-100%)' },
                    '100%': { transform: 'translateX(100%)' },
                  },
                }}
              />
            </motion.div>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            mt: 1.5,
            position: 'relative',
            zIndex: 1,
            width: '100%',
          }}
        >
          {steps.map((stepKey, index) => {
            const stepProgress = ((index + 1) / totalSteps) * 100;
            return (
              <Box
                key={stepKey}
                sx={{
                  position: 'absolute',
                  left: `${stepProgress}%`,
                  transform: 'translateX(-50%)',
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: index <= currentStep ? 1 : 0.4,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Box
                  sx={{
                    backgroundColor:
                      index <= currentStep ? 'white' : 'rgba(255,255,255,0.4)',
                    borderRadius: '50%',
                    height: index === currentStep ? 12 : 10,
                    width: index === currentStep ? 12 : 10,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow:
                      index <= currentStep
                        ? '0 2px 8px rgba(255,255,255,0.4)'
                        : 'none',
                    transform:
                      index === currentStep ? 'scale(1.2)' : 'scale(1)',
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              damping: 25,
              stiffness: 300,
              type: 'spring',
            }}
            style={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
